import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  QrCode,
  CheckCircle,
  Refresh,
  Close,
  ContentCopy,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import QRCode from 'qrcode';

/**
 * Modal para exibir QR Code de conexão WhatsApp
 * Seguindo padrões de design do projeto JBE Engenharia
 */
const QRCodeModal = ({
  open,
  onClose,
  qrCodeData,
  connectionStatus = 'connecting',
  instanceName = 'WhatsApp',
  onRefresh,
  refreshing = false,
}) => {
  const theme = useTheme();
  const canvasRef = useRef(null);
  const [qrCodeGenerated, setQrCodeGenerated] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Gera QR Code no canvas - versão mais robusta
  useEffect(() => {
    if (!qrCodeData?.qrCode || !open) {
      setQrCodeGenerated(false);
      setRetryCount(0);
      return;
    }

    // Reset contador ao abrir com novo QR Code
    setRetryCount(0);

    const generateQRCode = () => {
      if (!canvasRef.current || !open) {
        // Canvas ainda não está disponível ou modal não está aberto, tenta novamente
        if (open) setTimeout(generateQRCode, 50);
        return;
      }

      setQrCodeGenerated(false);
      
      // Limpa o canvas antes de gerar novo QR Code
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      
      QRCode.toCanvas(
        canvasRef.current,
        qrCodeData.qrCode,
        {
          width: 280,
          margin: 2,
          color: {
            dark: '#000000', // Usar preto fixo para melhor contraste
            light: '#FFFFFF', // Usar branco fixo para melhor contraste
          },
          errorCorrectionLevel: 'M',
        },
        (error) => {
          if (error) {
            console.error('Erro ao gerar QR Code (tentativa', retryCount + 1, '):', error);
            setQrCodeGenerated(false);
            // Tenta novamente em caso de erro (máximo 3 tentativas)
            if (retryCount < 3) {
              setRetryCount(prev => prev + 1);
              setTimeout(generateQRCode, 300 * (retryCount + 1)); // Delay progressivo
            }
          } else {
            setQrCodeGenerated(true);
            setRetryCount(0); // Reset contador de tentativas em caso de sucesso
          }
        }
      );
    };

    // Aguarda um frame de renderização antes de gerar
    requestAnimationFrame(() => {
      setTimeout(generateQRCode, 100);
    });
  }, [qrCodeData?.qrCode, open, retryCount]);



  const handleCopyPairingCode = () => {
    if (qrCodeData?.pairingCode) {
      navigator.clipboard.writeText(qrCodeData.pairingCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleClose = () => {
    setQrCodeGenerated(false);
    setCopySuccess(false);
    setRetryCount(0);
    // Limpa o canvas ao fechar
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <QrCode color="primary" />
            <Typography variant="h6" component="span">
              Conectar {instanceName}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Status de conexão */}
        {connectionStatus === 'open' && (
          <Alert 
            severity="success" 
            icon={<CheckCircle />}
            sx={{ mb: 3 }}
          >
            <Typography fontWeight={500}>
              WhatsApp conectado com sucesso!
            </Typography>
            <Typography variant="body2">
              A conexão foi estabelecida e está pronta para uso.
            </Typography>
          </Alert>
        )}

        {/* QR Code */}
        {qrCodeData?.qrCode && connectionStatus !== 'open' && (
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Box position="relative" mb={2}>
              <canvas
                ref={canvasRef}
                width={280}
                height={280}
                style={{
                  borderRadius: theme.shape.borderRadius,
                  border: `2px solid ${theme.palette.divider}`,
                  display: 'block',
                  backgroundColor: 'white',
                }}
              />
              
              {/* Loading overlay enquanto gera QR Code ou quando refreshing */}
              {(!qrCodeGenerated || refreshing) && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="rgba(255,255,255,0.9)"
                  borderRadius={1}
                >
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <CircularProgress size={32} />
                    <Typography variant="caption" color="text.secondary">
                      {refreshing ? 'Atualizando...' : 'Gerando QR Code...'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Código de pareamento */}
            {qrCodeData?.pairingCode && (
              <Box textAlign="center" mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Código de Pareamento:
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={qrCodeData.pairingCode}
                    variant="outlined"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleCopyPairingCode}
                    color={copySuccess ? 'success' : 'default'}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
                {copySuccess && (
                  <Typography variant="caption" color="success.main">
                    Copiado!
                  </Typography>
                )}
              </Box>
            )}

            {/* Status - apenas quando conectando */}
            {connectionStatus === 'connecting' && (
              <Box display="flex" alignItems="center" gap={1} justifyContent="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Aguardando conexão...
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Aguardando QR Code */}
        {!qrCodeData?.qrCode && connectionStatus !== 'open' && (
          <Box display="flex" alignItems="center" justifyContent="center" p={4}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Gerando código QR...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Box display="flex" gap={1} width="100%" justifyContent="space-between">
          <Button
            onClick={handleClose}
            variant="outlined"
            startIcon={<Close />}
          >
            {connectionStatus === 'open' ? 'Fechar' : 'Cancelar'}
          </Button>
          
          {onRefresh && connectionStatus !== 'open' && (
            <Button
              onClick={onRefresh}
              variant="outlined"
              startIcon={refreshing ? 
                <CircularProgress size={16} color="inherit" /> : 
                <Refresh />
              }
              disabled={refreshing}
            >
              {refreshing ? 'Atualizando...' : 'Atualizar QR'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default QRCodeModal;
